// src/App.js (Mobile Responsive Version - WITH INVOICE SETTINGS CONTROL PANEL)
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Import separated Login component
import Login from './components/Login';

// Import other components
import BikeInvoiceDemo from './Invoice/invoice'; 
import BikeBooking from './BookingManagement/booking';
import InvoicePreview from './Invoice/InvoicePreview';
import Reservations from './Reservations Management/Reservations'; 
import ReservationSettings from './Reservation Settings/ReservationSettings';
import CustomerBookingPage from './CustomerBooking/CustomerBookingPage';
import QuotationGenerator from './Bike Quotation Generator/QuotationGenerator'; 
import Dashboard from './Dashboard';
import BikeManagement from './Bike Management/BikeManagement';
import BikeAnalytics from './Analitics/BikeAnalytics';
import SimpleConfigForm from './Configuration/SimpleConfigForm';
import MyAccount from './My Account/MyAccount';
import ImageFieldsSettings from './Upload Doc Invoice/ImageFieldsSettings';
import FinanceManager from './Finance Manager/FinanceManager';
import InvoiceManager from './Invoice Management/InvoiceManager';
import InvoiceSettingsControlPanel from './Invoice Management/InvoiceSettingsControlPanel'; // ⭐ NEW - Invoice Settings

// Import contexts
import { ProfileProvider } from './ProfileContext';
import { BookingProvider } from "./BookingContext";
import { FirebaseProvider } from './FirebaseContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import icons
import { 
  Bike, 
  FileText, 
  Users, 
  Calendar,
  UploadCloud,
  LayoutDashboard,
  Settings,
  BarChart3,
  Sliders,
  User,
  DollarSign,
  LogOut,
  Shield,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  Check,
  Database,
  Wifi,
  WifiOff,
  UserCheck,
  UserX,
  Mail,
  Building,
  Info,
  CheckCircle,
  AlertTriangle,
  Loader,
  Edit3,
  AlertCircle,
  Globe,
  Server,
  Lock,
  Calculator,
  Share2,
  Copy,
  ExternalLink,
  QrCode,
  X,
  Phone,
  Receipt,
  Menu,
  ChevronRight,
  Palette // ⭐ NEW - For Invoice Settings icon
} from 'lucide-react';

// ===========================================
// MENU ITEMS (UPDATED WITH INVOICE SETTINGS)
// ===========================================
const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/' },
  { id: 'bikeManagement', label: 'Bike Management', icon: <Settings size={20} />, path: '/bikeManagement' },
  { id: 'bikeAnalytics', label: 'Bike Analytics', icon: <BarChart3 size={20} />, path: '/bikeAnalytics' },
  { id: 'invoice', label: 'Invoice', icon: <Receipt size={20} />, path: '/invoice' },
  { id: 'invoiceSettings', label: 'Invoice Settings', icon: <Palette size={20} />, path: '/invoice-settings' }, // ⭐ NEW
  { id: 'users', label: 'User Management', icon: <Users size={20} />, path: '/users' },
  { id: 'bookings', label: 'Bookings', icon: <Calendar size={20} />, path: '/bookings' },
  { id: 'reservations', label: 'Reservations', icon: <Calendar size={20} />, path: '/reservations' },
  { id: 'reservationSettings', label: 'Reservation Settings', icon: <Calculator size={20} />, path: '/reservation-settings' },
  { id: 'financeManager', label: 'Finance Manager', icon: <DollarSign size={20} />, path: '/financeManager' },
  { id: 'quotationGenerator', label: 'Quotation Generator', icon: <FileText size={20} />, path: '/quotationGenerator' },
  { id: 'apiConfig', label: 'API Configuration', icon: <Sliders size={20} />, path: '/apiConfig' },
];

// ===========================================
// PUBLIC ROUTE CHECKER
// ===========================================
const PublicRouteChecker = () => {
  const location = useLocation();
  const isPublicRoute = location.pathname === '/customer-booking';
  
  if (isPublicRoute) {
    return <CustomerBookingPage />;
  }
  
  return null;
};

// ===========================================
// SHARE BOOKING FORM MODAL COMPONENT (Mobile Responsive)
// ===========================================
const ShareBookingModal = ({ isOpen, onClose, currentCompany, getCompanyContext }) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  if (!isOpen) return null;

  const companyId = getCompanyContext();
  const baseUrl = 'https://easyrentaly.com';
  const bookingUrl = `${baseUrl}/customer-booking?company=${companyId}&name=${encodeURIComponent(currentCompany?.name || 'Bike Rental')}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = bookingUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(bookingUrl, '_blank');
  };

  const generateQRCode = () => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(bookingUrl)}`;
    return qrUrl;
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Book Your Bike Rental - ${currentCompany?.name}`);
    const body = encodeURIComponent(`Hi there!

You can easily book your bike rental online using this link:

${bookingUrl}

It's quick, easy, and available 24/7!

Best regards,
${currentCompany?.name}`);
    
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(`🚴‍♂️ Book your bike rental online!

${bookingUrl}

Quick & easy booking available 24/7

- ${currentCompany?.name}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Modal Header - Sticky */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
              <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 mr-2" />
              Share Booking Form
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Company Info */}
          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
            <div className="flex items-center text-orange-800">
              <Building size={16} className="mr-2" />
              <span className="font-medium">{currentCompany?.name}</span>
            </div>
            <div className="text-xs text-orange-600 mt-1">
              Customer booking form for your company
            </div>
          </div>

          {/* Booking URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Booking Link
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={bookingUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-800 focus:outline-none"
              />
              <button
                onClick={handleCopyLink}
                className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  copied 
                    ? 'bg-green-500 text-white' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {copied && (
              <p className="text-green-600 text-sm mt-1">Link copied to clipboard!</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center justify-center px-3 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors text-sm"
            >
              <ExternalLink size={16} className="mr-2" />
              Preview
            </button>
            <button
              onClick={() => setShowQR(!showQR)}
              className="flex items-center justify-center px-3 py-3 bg-gradient-to-r from-orange-400 to-purple-600 text-white rounded-lg hover:from-orange-500 hover:to-purple-700 transition-colors text-sm"
            >
              <QrCode size={16} className="mr-2" />
              QR Code
            </button>
          </div>

          {/* QR Code Display */}
          {showQR && (
            <div className="text-center bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="mb-3">
                <img
                  src={generateQRCode()}
                  alt="QR Code for booking form"
                  className="mx-auto mb-2 border border-gray-600 rounded"
                  onError={(e) => {
                    console.error('QR Code failed to load');
                    e.target.src = `https://chart.googleapis.com/chart?chs=250x250&cht=qr&chl=${encodeURIComponent(bookingUrl)}`;
                  }}
                />
              </div>
              <p className="text-sm text-gray-400 mb-2">
                Customers can scan this QR code to access the booking form
              </p>
              <div className="text-xs text-gray-500 bg-gray-700 rounded p-2 break-all">
                <p>QR Code contains: {bookingUrl.length > 50 ? bookingUrl.substring(0, 50) + '...' : bookingUrl}</p>
              </div>
            </div>
          )}

          {/* Share Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Share via
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleEmailShare}
                className="flex items-center justify-center px-3 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                <Mail size={16} className="mr-2" />
                Email
              </button>
              <button
                onClick={handleWhatsAppShare}
                className="flex items-center justify-center px-3 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                <Phone size={16} className="mr-2" />
                WhatsApp
              </button>
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
            <h4 className="font-medium text-orange-800 mb-2 text-sm">How to use:</h4>
            <ul className="text-xs sm:text-sm text-orange-700 space-y-1">
              <li>• Share the link with customers via email, SMS, or social media</li>
              <li>• Print the QR code for physical locations</li>
              <li>• Embed on your website or social media</li>
              <li>• Customers can book 24/7 without staff assistance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// USER MANAGEMENT COMPONENT
// ===========================================
const UserManagement = () => {
  const { 
    currentUser, 
    currentCompany, 
    canManageUsers,
    getCompanyContext,
    refreshUser
  } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    role: 'user',
    permissions: [],
    isActive: true
  });

  const availablePages = [
    { id: 'dashboard', label: 'Dashboard', description: 'Main dashboard access', icon: LayoutDashboard },
    { id: 'bikeManagement', label: 'Bike Management', description: 'Manage bikes and inventory', icon: Settings },
    { id: 'bikeAnalytics', label: 'Bike Analytics', description: 'View analytics and reports', icon: BarChart3 },
    { id: 'invoice', label: 'Invoice', description: 'Create and manage invoices', icon: Receipt },
    { id: 'invoiceSettings', label: 'Invoice Settings', description: 'Configure invoice display', icon: Palette }, // ⭐ NEW
    { id: 'bookings', label: 'Bookings', description: 'Manage bookings and reservations', icon: Calendar },
    { id: 'reservations', label: 'Reservations', description: 'Handle reservation system', icon: Calendar },
    { id: 'reservationSettings', label: 'Reservation Settings', description: 'Configure reservation rules', icon: Calculator },
    { id: 'financeManager', label: 'Finance Manager', description: 'Financial management tools', icon: DollarSign },
    { id: 'quotationGenerator', label: 'Quotation Generator', description: 'Generate quotes', icon: FileText },
    { id: 'apiConfig', label: 'API Configuration', description: 'System configuration', icon: Sliders },
    { id: 'documentFields', label: 'Document Fields', description: 'Manage document field settings', icon: Settings }
  ];

  const loadUsers = React.useCallback(() => {
    setLoading(true);
    try {
      const companyId = getCompanyContext();
      const savedUsers = localStorage.getItem('bikeManagerUsers');
      let allUsers = savedUsers ? JSON.parse(savedUsers) : [];
      
      const companyUsers = allUsers.filter(user => user.companyId === companyId);
      setUsers(companyUsers);
      console.log('👥 Loaded', companyUsers.length, 'users for company:', currentCompany?.name);
    } catch (error) {
      console.error('❌ Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getCompanyContext, currentCompany]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (!canManageUsers()) {
    return (
      <div className="text-center py-12">
        <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
        <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
        <p className="text-gray-500 text-lg">You don't have permission to access user management.</p>
        <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-lg p-4 sm:p-6 border border-gray-600">
        <div className="flex justify-between items-start">
          <div className="w-full">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">User Management</h2>
              <div className="flex items-center text-green-400 text-xs sm:text-sm">
                <Database size={14} className="mr-1" />
                <span className="hidden sm:inline">Multi-Tenant</span>
              </div>
              {currentCompany && (
                <div className="flex items-center text-blue-400 text-xs sm:text-sm">
                  <Building size={14} className="mr-1" />
                  <span className="truncate max-w-[150px]">{currentCompany.name}</span>
                </div>
              )}
            </div>
            <p className="text-gray-400 mb-4 text-sm sm:text-base">
              Manage users within your company - complete data isolation
            </p>
            
            {/* Statistics - Mobile Responsive */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-gray-400 text-xs sm:text-sm">Total Users</div>
                <div className="text-xl sm:text-2xl font-bold text-white">{users.length}</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-gray-400 text-xs sm:text-sm">Active Users</div>
                <div className="text-xl sm:text-2xl font-bold text-green-400">
                  {users.filter(u => u.isActive).length}
                </div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-gray-400 text-xs sm:text-sm">Administrators</div>
                <div className="text-xl sm:text-2xl font-bold text-purple-400">
                  {users.filter(u => u.role === 'admin').length}
                </div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-gray-400 text-xs sm:text-sm">Regular Users</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-400">
                  {users.filter(u => u.role === 'user').length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// APP CONTENT COMPONENT (Mobile Responsive)
// ===========================================
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const { 
    currentUser, 
    currentCompany, 
    loading, 
    bikeDataLoaded,
    documentFieldsReady,
    authInitialized,
    logout, 
    getCompanyContext, 
    hasPermission 
  } = useAuth();

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth < 1024) {
        const sidebar = document.getElementById('mobile-sidebar');
        const menuButton = document.getElementById('menu-button');
        
        if (sidebar && !sidebar.contains(event.target) && !menuButton.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  useEffect(() => {
    if (currentUser && !loading && authInitialized) {
      const currentPath = location.pathname;
      const currentMenuItem = menuItems.find(item => item.path === currentPath);
      
      if (currentMenuItem) {
        setActiveMenu(currentMenuItem.id);
      } else if (currentPath === '/myAccount') {
        setActiveMenu('myAccount');
      } else if (currentPath === '/settings') {
        setActiveMenu('settings');
      } else {
        setActiveMenu('dashboard');
      }
    }
  }, [location.pathname, currentUser, loading, authInitialized]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading multi-tenant authentication...</p>
          <p className="text-gray-400 text-sm mt-2">Initializing secure company isolation</p>
        </div>
      </div>
    );
  }

  if (currentUser && authInitialized && (!bikeDataLoaded || !documentFieldsReady)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Initializing multi-tenant services...</p>
          <p className="text-gray-400 text-sm mt-2">Setting up secure data isolation for {currentCompany?.name}</p>
          <div className="mt-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4 max-w-md mx-auto">
            <div className="space-y-3 text-sm">
              <div className={`flex items-center justify-between ${bikeDataLoaded ? 'text-green-400' : 'text-yellow-400'}`}>
                <div className="flex items-center">
                  {bikeDataLoaded ? <CheckCircle size={16} className="mr-2" /> : <Loader size={16} className="mr-2 animate-spin" />}
                  Bike Management
                </div>
                <span className="text-xs">{bikeDataLoaded ? 'Ready' : 'Loading...'}</span>
              </div>
              <div className={`flex items-center justify-between ${documentFieldsReady ? 'text-green-400' : 'text-yellow-400'}`}>
                <div className="flex items-center">
                  {documentFieldsReady ? <CheckCircle size={16} className="mr-2" /> : <Loader size={16} className="mr-2 animate-spin" />}
                  Document Fields
                </div>
                <span className="text-xs">{documentFieldsReady ? 'Ready' : 'Loading...'}</span>
              </div>
              <div className="text-xs text-blue-200 pt-2 border-t border-blue-700">
                🔒 All data isolated to {currentCompany?.name}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Login />;
  }

  const handleMenuClick = (itemId) => {
    const menuItem = menuItems.find(item => item.id === itemId);
    if (menuItem) {
      navigate(menuItem.path);
    }
    setSidebarOpen(false);
  };

  const navigateToMyAccount = () => {
    navigate('/myAccount');
    setSidebarOpen(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleShareBookingForm = () => {
    setShowShareModal(true);
  };

  const getCurrentPageInfo = () => {
    const currentPath = location.pathname;
    
    if (currentPath === '/myAccount') {
      return { label: 'My Account', icon: <User size={20} /> };
    }
    if (currentPath === '/settings') {
      return { label: 'Document Fields Settings', icon: <Settings size={20} /> };
    }
    
    const currentMenuItem = menuItems.find(item => item.path === currentPath);
    return currentMenuItem || { label: 'Dashboard', icon: <LayoutDashboard size={20} /> };
  };

  const currentPage = getCurrentPageInfo();
  const isSpecialPage = location.pathname === '/myAccount' || location.pathname === '/settings';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white">
      {/* Header - Mobile Responsive */}
      <header className="bg-gray-800 border-b border-gray-700 shadow-lg sticky top-0 z-40">
        <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
          {/* Mobile Menu Button */}
          <button
            id="menu-button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors mr-2"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo - Responsive */}
          <h1 
            className="text-lg sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600 cursor-pointer flex items-center flex-1 lg:flex-none"
            onClick={() => navigate('/')}
          >
            <span className="hidden sm:inline">Bike Manager</span>
            <span className="sm:hidden">BM</span>
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-blue-400 flex items-center">
              <Server className="mr-1" size={12} />
              <span className="hidden sm:inline">Multi-Tenant</span>
            </span>
          </h1>

          {/* Header Actions - Mobile Optimized */}
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
            {/* User Info - Hidden on small mobile */}
            <div className="hidden md:block text-right">
              <div className="text-sm font-medium text-white flex items-center">
                {currentUser.username}
                {bikeDataLoaded && documentFieldsReady && (
                  <div className="ml-2 flex items-center text-green-400 text-xs">
                    <CheckCircle size={12} className="mr-1" />
                    <span className="hidden lg:inline">All Services Ready</span>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-400 capitalize flex items-center">
                <Building size={12} className="mr-1" />
                {currentUser.role} • {currentCompany?.name}
              </div>
            </div>
            
            {/* Share Booking Form Button */}
            <button 
              onClick={handleShareBookingForm}
              className="px-2 sm:px-5 py-1.5 sm:py-2.5 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-lg text-white transition-all duration-200 flex items-center space-x-1 sm:space-x-2 shadow-md hover:shadow-lg font-medium text-xs sm:text-sm"
              title="Share Customer Booking Form"
            >
              <Share2 size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Share Booking</span>
            </button>
            
            <button 
              onClick={handleSettingsClick}
              className={`p-1.5 sm:p-2 ${location.pathname === '/settings' ? 'bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'} rounded-lg text-gray-300 hover:text-white transition-colors`}
              title="Document Fields Settings"
            >
              <Settings size={18} className="sm:w-5 sm:h-5" />
            </button>
            
            <button 
              onClick={navigateToMyAccount}
              className={`p-1.5 sm:p-2 ${location.pathname === '/myAccount' ? 'bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'} rounded-lg text-gray-300 hover:text-white transition-colors`}
              title="My Account"
            >
              <User size={18} className="sm:w-5 sm:h-5" />
            </button>

            <button 
              onClick={handleLogout}
              className="p-1.5 sm:p-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors"
              title="Logout"
            >
              <LogOut size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Mobile User Info Bar - Visible only on small screens */}
        <div className="md:hidden px-3 pb-2 text-xs border-t border-gray-700 pt-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-300">
              <User size={12} className="mr-1" />
              <span className="truncate max-w-[120px]">{currentUser.username}</span>
            </div>
            <div className="flex items-center text-gray-400">
              <Building size={12} className="mr-1" />
              <span className="truncate max-w-[120px]">{currentCompany?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Sidebar Overlay for Mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Mobile Responsive */}
        <aside 
          id="mobile-sidebar"
          className={`
            fixed lg:static inset-y-0 left-0 z-40
            w-64 sm:w-72 min-h-[calc(100vh-73px)] sm:min-h-[calc(100vh-80px)]
            bg-gray-800 border-r border-gray-700 shadow-lg
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            overflow-y-auto
          `}
        >
          <nav className="p-3">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`flex items-center w-full px-3 py-3 my-1 rounded-lg transition-colors duration-200 text-sm sm:text-base ${
                  activeMenu === item.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-md'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <span className="flex items-center justify-center w-8 h-8">
                  {item.icon}
                </span>
                <span className="ml-3 font-medium">{item.label}</span>
              </button>
            ))}
            
            {/* Multi-Tenant Indicator */}
            <div className="mt-6 p-3 bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg border border-blue-700">
              <div className="text-blue-300 text-xs font-medium mb-1 flex items-center">
                <Lock className="mr-1" size={12} />
                Tenant Isolation Active
              </div>
              <div className="text-blue-200 text-xs break-words">
                Data isolated to: {currentCompany?.name}
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content - Mobile Responsive */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-x-hidden">
          {/* Page Header - Mobile Optimized */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center">
                <span className="mr-2 sm:mr-3 bg-gray-700 p-2 rounded-lg text-orange-500">
                  {currentPage.icon}
                </span>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {currentPage.label}
                  </h2>
                  {bikeDataLoaded && documentFieldsReady && (
                    <div className="flex items-center text-green-400 text-xs sm:text-sm mt-1">
                      <CheckCircle size={14} className="mr-1" />
                      <span>Multi-Tenant Services Active</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center text-blue-400 text-sm">
                <Building size={16} className="mr-1" />
                <span className="truncate max-w-[200px]">{currentCompany?.name}</span>
              </div>
            </div>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-orange-500 to-red-600 rounded-full mt-2"></div>
          </div>

          {/* Content Area - Responsive */}
          <div className={`${isSpecialPage ? '' : 'bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden'}`}>
            <div className={`${isSpecialPage ? '' : 'p-3 sm:p-4 md:p-6'}`}>
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <Dashboard 
                      setActiveMenu={setActiveMenu} 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/bikeManagement" 
                  element={
                    <BikeManagement 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/bikeAnalytics" 
                  element={
                    <BikeAnalytics 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/invoice" 
                  element={
                    <InvoiceManager 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                
                {/* ⭐ NEW - Invoice Settings Control Panel Route */}
                <Route 
                  path="/invoice-settings" 
                  element={
                    <InvoiceSettingsControlPanel 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                
                <Route path="/users" element={<UserManagement />} />
                <Route 
                  path="/bookings" 
                  element={
                    <BikeBooking 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/reservations" 
                  element={
                    <Reservations 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/reservation-settings" 
                  element={
                    <ReservationSettings 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/financeManager" 
                  element={
                    <FinanceManager 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route 
                  path="/quotationGenerator" 
                  element={
                    <QuotationGenerator 
                      currentUser={currentUser}
                      currentCompany={currentCompany}
                      getCompanyContext={getCompanyContext}
                      hasPermission={hasPermission}
                    />
                  } 
                />
                <Route path="/apiConfig" element={<SimpleConfigForm />} />
                <Route path="/myAccount" element={<MyAccount />} />
                <Route path="/settings" element={<ImageFieldsSettings />} />
              </Routes>
            </div>
          </div>
        </main>
      </div>

      {/* Share Booking Form Modal */}
      <ShareBookingModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        currentCompany={currentCompany}
        getCompanyContext={getCompanyContext}
      />

      {/* Multi-Tenant Status Notification - Mobile Positioned */}
      {bikeDataLoaded && documentFieldsReady && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-auto bg-green-600/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg opacity-90 z-50 transition-all">
          <div className="flex items-center space-x-2 text-sm">
            <CheckCircle size={16} />
            <span className="font-medium truncate">
              Multi-tenant services ready for {currentCompany?.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================
// MAIN APP COMPONENT WITH PUBLIC ROUTES
// ===========================================
const App = () => {
  return (
    <Router>
      <FirebaseProvider>
        <AuthProvider>
          <ProfileProvider>
            <BookingProvider>
              <Routes>
                {/* Public Route - Customer Booking Form */}
                <Route path="/customer-booking" element={<CustomerBookingPage />} />
                
                {/* All other routes go through AppContent (authenticated) */}
                <Route path="/*" element={<AppContent />} />
              </Routes>
            </BookingProvider>
          </ProfileProvider>
        </AuthProvider>
      </FirebaseProvider>
    </Router>
  );
};

export default App;